import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/ar";
import { getNumberLocale } from "src/utils/numberFormat";
import type { CustomList } from "../../EngagementInterfaces";

dayjs.extend(customParseFormat);

export const DATA_SCIENCE_LIST_TYPE = "Data Science";
export const ARCHIVED_LIST_STATUS = "archive";
export const CREATED_BY_DATATRAM_TAG = "created by ask datatram ai";

const CREATED_AT_FORMAT = "MMM D, YYYY [at] hh:mm A";
const CREATED_AT_FORMAT_AR = "D MMMM YYYY [،] hh:mm A";
const DATE_PART_ORDER = ["day", "month", "year"];
const ARABIC_INDIC_DIGITS = /[٠-٩]/g;

export type AudienceListKind = "predicted" | "uploaded" | "segment";

export type AudienceKindCounts = Record<AudienceListKind, number>;

export const AUDIENCE_KIND_LABEL_KEYS: Record<AudienceListKind, string> = {
  predicted: "audienceOverview.typePredicted",
  uploaded: "audienceOverview.typeUploaded",
  segment: "audienceOverview.typeSegment",
};

export type AudienceSortOrder = "newest" | "oldest";

export const getAudienceListKind = (listType: string | null | undefined): AudienceListKind => {
  if (listType === DATA_SCIENCE_LIST_TYPE) {
    return "predicted";
  }
  return listType?.toLowerCase().includes("segment") ? "segment" : "uploaded";
};

export const isPredictedList = (list: Pick<CustomList, "list_type">): boolean =>
  getAudienceListKind(list.list_type) === "predicted";

export const isArchivedList = (list: Pick<CustomList, "status">): boolean =>
  list.status === ARCHIVED_LIST_STATUS;

export const isCreatedByDatatram = (list: Pick<CustomList, "tags">): boolean =>
  (list.tags ?? []).some(tag => tag.trim().toLowerCase() === CREATED_BY_DATATRAM_TAG);

export const countListsByKind =(lists: CustomList[]): AudienceKindCounts =>
  lists.reduce<AudienceKindCounts>(
    (counts, list) => {
      counts[getAudienceListKind(list.list_type)] += 1;
      return counts;
    },
    { predicted: 0, uploaded: 0, segment: 0 },
  );

export const parseCreatedAt = (value: string | null | undefined): Dayjs | null => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value, CREATED_AT_FORMAT);
  if (parsed.isValid()) {
    return parsed;
  }
  const arabic = dayjs(
    value.replace(ARABIC_INDIC_DIGITS, digit => String(digit.charCodeAt(0) - 0x660)),
    CREATED_AT_FORMAT_AR,
    "ar",
  );
  if (arabic.isValid()) {
    return arabic;
  }
  const fallback = dayjs(value);
  return fallback.isValid() ? fallback : null;
};

export const formatCreatedDate = (createdAt: Dayjs, language: string): string =>
  new Intl.DateTimeFormat(getNumberLocale(language), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
    .formatToParts(createdAt.toDate())
    .filter(part => part.type === "day" || part.type === "month" || part.type === "year")
    .sort((a, b) => DATE_PART_ORDER.indexOf(a.type) - DATE_PART_ORDER.indexOf(b.type))
    .map(part => part.value)
    .join("-");
